import Accordion from "@/components/Accordion";
import { LanguageConfigModel } from "@/pages/Messaging/types";
import React from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

type Field = { label: string; field: keyof LanguageConfigModel; default: string };
type FieldGroup = { heading: string; fields: Field[] };

const fieldGroups: FieldGroup[] = [
  {
    heading: "Welcome & Status",
    fields: [
      { label: "Agent Welcome Message", field: "agentWelcomeMessage", default: "Hello @VisitorName, how can I help you today?" },
      { label: "Agent Joined", field: "agentJoinedText", default: "@AgentName has joined" },
      { label: "Agent Exited", field: "agentExitedText", default: "@AgentName has exited" },
      { label: "Typing Indicator", field: "typingIndicatorText", default: "@AgentName is typing..." },
    ]
  },
  {
    heading: "Chat Close",
    fields: [
      { label: "Agent Closed Chat", field: "chatClosedText", default: "This chat has been closed." },
      { label: "System Closed Chat", field: "systemClosedChatText", default: "This chat has been closed by the system" },
      { label: "Closed Due to Inactivity", field: "inactiveChatClosedText", default: "Your chat has been ended by the system because of inactivity" },
      { label: "Close Chat Confirmation", field: "closeChatConfirmText", default: "Are you sure you want to close this chat?" },
      { label: "Close Chat Button", field: "closeChatButtonText", default: "Close Chat" },
      { label: "Close Chat Header", field: "closeChatHeaderText", default: "Close Chat" },
      { label: "Yes Button", field: "yesButtonText", default: "Yes" },
      { label: "No Button", field: "noButtonText", default: "No" },
    ]
  },
  {
    heading: "Transfers",
    fields: [
      { label: "Transfer to Agent", field: "agentTransferText", default: "@AgentName is transferring engagement to agent @NewAgentName" },
      { label: "Agent Transfer to Group", field: "agentGroupTransferText", default: "@AgentName is transferring engagement to @GroupName" },
      { label: "System Transfer to Group", field: "systemGroupTransferText", default: "The engagement is being transferred to @GroupName" },
      { label: "Transfer to Tier", field: "transferToTierText", default: "@AgentName is transferring engagement to tier @TierNumber" },
    ]
  },
  {
    heading: "File Uploads",
    fields: [
      { label: "Agent Uploading File", field: "agentUploadingFileText", default: "@AgentName is currently uploading @FileName" },
      { label: "Agent Uploaded File", field: "agentUploadedFileText", default: "@AgentName has successfully uploaded a file. Click to download @FileName" },
      { label: "Visitor Uploaded File", field: "visitorFileUploadText", default: "@VisitorName has successfully uploaded @FileName" },
      { label: "File Too Large", field: "fileTooLargeText", default: "The file you are attempting to upload is larger than permitted. Your Agent will assist with uploading your file." },
      { label: "Virus Detected", field: "virusScanFoundText", default: "File upload failed: Virus detected" },
      { label: "Virus Scan Error", field: "virusScanErrorText", default: "File upload failed: Virus scan error" },
      { label: "Virus Scan Unknown", field: "virusScanUnknownText", default: "File upload failed: Virus scan result unknown" },
    ]
  },
  {
    heading: "Menus & Actions",
    fields: [
      { label: "Notice Label", field: "fromText", default: "Notice" },
      { label: "Menu: Print", field: "menuPrintText", default: "Print" },
      { label: "Menu: Email Transcript", field: "menuEmailTranscriptText", default: "Email Transcript" },
      { label: "Menu: Popout", field: "menuPopoutText", default: "Popout" },
      { label: "Page Push Prefix", field: "pagePushText", default: "Please visit this page:" },
      { label: "Secure Form Request", field: "secureFormRequestText", default: "@AgentName has requested you fill out a secure form" },
    ]
  },
  {
    heading: "Timestamps",
    fields: [
      { label: "'Today at' Label", field: "todayAtText", default: "Today at" },
      { label: "'Yesterday at' Label", field: "yesterdayAtText", default: "Yesterday at" },
    ]
  },
];

export function Notices(props: Props) {
  const workingLanguageConfig = props.workingChatWindow.languageConfig || {};

  const updateLanguageConfig = (updates: Partial<LanguageConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      languageConfig: { ...workingLanguageConfig, ...updates }
    });
  };

  return (
    <Accordion.Item eventKey="notices" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Notices & System Messages</Accordion.Title>
        <Accordion.SubTitle>
          Customize automated messages shown during a conversation. Variables like @AgentName and @VisitorName are substituted at runtime.
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-6 pb-2">
          {fieldGroups.map((group) => (
            <div key={group.heading}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
                {group.heading}
              </div>
              <div className="space-y-3">
                {group.fields.map(({ label, field, default: defaultVal }) => (
                  <div key={field}>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-sm font-medium">{label}</div>
                      {!(workingLanguageConfig[field] as string) && (
                        <div className="text-xs text-slate-400 truncate max-w-[55%]" title={defaultVal}>
                          default: {defaultVal}
                        </div>
                      )}
                    </div>
                    <TextInput
                      value={(workingLanguageConfig[field] as string) ?? ""}
                      setValue={(value) => updateLanguageConfig({ [field]: value })}
                      showEmojiButton={false}
                      ariaLabel={label}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
